import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabaseClient";
import { useAuthSafe } from "@/context/auth.js";
import useOrgEntitlements from "@/hooks/useOrgEntitlements.js";
import UpgradeToProButton from "@/components/Billing/UpgradeToProButton";

const INVITE_API_URL = "/api/invite-tracker";

function normalizeEmail(value) {
  return String(value || "").trim().toLowerCase();
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || "").trim());
}

function pickPersonalLabel(row) {
  const nombre = String(row?.nombre || "").trim();
  const apellido = String(row?.apellido || "").trim();
  const email = String(row?.email || "").trim();
  const fullName = [nombre, apellido].filter(Boolean).join(" ").trim();

  if (fullName && email) return `${fullName} - ${email}`;
  return email || fullName || "(sin datos)";
}

function normalizePlanLabel(planCode) {
  const value = String(planCode || "").trim().toLowerCase();
  if (value === "pro") return "PRO";
  if (value === "enterprise_100") return "ENTERPRISE 100";
  if (value === "enterprise") return "ENTERPRISE";
  if (value === "elite_plus") return "ELITE PLUS";
  if (value === "elite") return "ELITE";
  if (value === "starter") return "STARTER";
  if (value === "free") return "FREE";
  return value ? value.toUpperCase() : "-";
}

export default function InvitarTracker() {
  // =========================
  // HOOKS BASE
  // =========================
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuthSafe();
  const {
    entitlements,
    loading: entitlementsLoading,
    error: entitlementsError,
    canInviteTrackers,
  } = useOrgEntitlements();

  const inviteInFlight = useRef(false);
  const sentRecipients = useRef(new Set());
  const [sentRecipient, setSentRecipient] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loadingPeople, setLoadingPeople] = useState(true);
  const [people, setPeople] = useState([]);
  const [activeAssignaciones, setActiveAssignaciones] = useState([]);
  const [trackerCount, setTrackerCount] = useState(null);
  const [trackerCountOrgId, setTrackerCountOrgId] = useState(null);
  const [trackerCountError, setTrackerCountError] = useState(null);
  const [loadingTrackerCount, setLoadingTrackerCount] = useState(true);
  const [selectedPersonKey, setSelectedPersonKey] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [okMsg, setOkMsg] = useState(null);
  const [errMsg, setErrMsg] = useState(null);
  const [inviteLink, setInviteLink] = useState("");
  const [inviteMeta, setInviteMeta] = useState(null);
  const [trackerIdentityBlocked, setTrackerIdentityBlocked] = useState(false);
  const [pairingCode, setPairingCode] = useState("");
  const [pairingCodeId, setPairingCodeId] = useState("");
  const [pairingCodeExpiresAt, setPairingCodeExpiresAt] = useState("");
  const [pairingLoading, setPairingLoading] = useState(false);
  const [pairingError, setPairingError] = useState(null);

  // =========================
  // DERIVADOS BASE
  // =========================
  const safeMaxTrackers = Number(entitlements?.max_trackers ?? 0);

  const planCode = String(
    entitlements?.plan_code ||
      entitlements?.plan ||
      entitlements?.subscription_plan ||
      "free"
  )
    .trim()
    .toLowerCase();

  const normalizedPlanStatus = String(entitlements?.plan_status || "free")
    .trim()
    .toLowerCase();

  const isCancellationScheduled = Boolean(entitlements?.cancel_at_period_end);
  const isActive = normalizedPlanStatus === "active";
  const permissionsLoading = auth?.loading || entitlementsLoading;

  // =========================
  // MEMOS
  // =========================
  // =========================
  const orgId = useMemo(() => {
    const id =
      auth?.orgId ||
      auth?.currentOrgId ||
      auth?.org?.id ||
      auth?.org_id ||
      auth?.currentOrg?.org_id ||
      auth?.currentOrg?.id ||
      "";

    return String(id || "").trim();
  }, [auth]);

  const lang = useMemo(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const queryLang = String(params.get("lang") || "").trim().toLowerCase();
      if (["es", "en", "fr"].includes(queryLang)) return queryLang;
    } catch {
      // no-op
    }

    const currentLang = String(i18n?.resolvedLanguage || i18n?.language || "es")
      .trim()
      .toLowerCase();

    if (currentLang.startsWith("en")) return "en";
    if (currentLang.startsWith("fr")) return "fr";
    return "es";
  }, [i18n]);

  const hasActiveAssignmentsInOrg = useMemo(() => {
    return activeAssignaciones.length > 0;
  }, [activeAssignaciones]);

  const inviteBlockedByPlan = useMemo(() => {
    return !permissionsLoading && !canInviteTrackers;
  }, [permissionsLoading, canInviteTrackers]);

  const trackerCountUnavailable = useMemo(() => {
    return (
      trackerCount === null ||
      trackerCountError !== null ||
      loadingTrackerCount ||
      trackerCountOrgId !== orgId
    );
  }, [loadingTrackerCount, orgId, trackerCount, trackerCountError, trackerCountOrgId]);

  const trackerLimitReached = useMemo(() => {
    if (loadingTrackerCount || trackerCountUnavailable || trackerCount === null) return false;
    if (!Number.isInteger(trackerCount) || trackerCount < 0) return false;
    if (safeMaxTrackers <= 0) return true;
    return trackerCount >= safeMaxTrackers;
  }, [loadingTrackerCount, safeMaxTrackers, trackerCount, trackerCountUnavailable]);

  const inviteAccessBlock = useMemo(() => {
    if (permissionsLoading) {
      return {
        kind: "loading",
        title: t("inviteTracker.org.syncing", {
          defaultValue: "Sincronizando organización y plan...",
        }),
        body: t("inviteTracker.org.syncing", {
          defaultValue: "Sincronizando organización y plan...",
        }),
      };
    }

    if (entitlementsError) {
      return {
        kind: "error",
        title: t("inviteTracker.errors.planLoadFailed", {
          defaultValue: "No se pudo cargar la información del plan para esta organización.",
        }),
        body: t("inviteTracker.errors.planLoadFailed", {
          defaultValue: "No se pudo cargar la información del plan para esta organización.",
        }),
      };
    }

    if (!canInviteTrackers) {
      return {
        kind: "plan",
        title: t("inviteTracker.plan.permissionDisabledTitle", {
          defaultValue: "Permiso de invitación no habilitado.",
        }),
        body: t("inviteTracker.plan.permissionDisabledBody", {
          defaultValue: "Esta organización no puede invitar trackers en este momento.",
        }),
      };
    }

    if (trackerCountError) {
      return {
        kind: "error",
        title: t("inviteTracker.errors.trackerCountFailed", {
          defaultValue: "No se pudo consultar el cupo de trackers disponible.",
        }),
        body: t("inviteTracker.errors.trackerCountFailed", {
          defaultValue: "No se pudo consultar el cupo de trackers disponible.",
        }),
      };
    }

    if (loadingTrackerCount || trackerCountUnavailable) {
      return {
        kind: "loading",
        title: t("inviteTracker.usage.loading", {
          defaultValue: "Calculando uso…",
        }),
        body: t("inviteTracker.usage.loading", {
          defaultValue: "Calculando uso…",
        }),
      };
    }

    if (trackerLimitReached) {
      return {
        kind: "limit",
        title: t("inviteTracker.usage.limitReached", {
          defaultValue: "Límite alcanzado",
        }),
        body: t("inviteTracker.usage.upgradeLimit", {
          defaultValue: "Tu plan actual llegó al límite. Actualiza para agregar más trackers.",
        }),
      };
    }

    return null;
  }, [
    permissionsLoading,
    entitlementsError,
    canInviteTrackers,
    loadingTrackerCount,
    trackerCountError,
    trackerCountUnavailable,
    trackerLimitReached,
    t,
  ]);

  const trackersUsageLabel = useMemo(() => {
    if (trackerCountUnavailable) return "—";
    return `${trackerCount} / ${safeMaxTrackers}`;
  }, [safeMaxTrackers, trackerCount, trackerCountUnavailable]);

  const activeAssignmentByPersonId = useMemo(() => {
    const map = new Map();

    for (const assignment of activeAssignaciones) {
      const personId = String(assignment?.personal_id || "").trim();
      if (personId && !map.has(personId)) {
        map.set(personId, assignment);
      }
    }

    return map;
  }, [activeAssignaciones]);

  const peopleWithActiveAssignments = useMemo(() => {
    const filtered = people.filter((person) => {
      const personId = String(person?.id || "").trim();
      return personId && activeAssignmentByPersonId.has(personId);
    });

    filtered.sort((a, b) => pickPersonalLabel(a).localeCompare(pickPersonalLabel(b)));
    return filtered;
  }, [people, activeAssignmentByPersonId]);

  const inviteOptions = useMemo(() => {
    return peopleWithActiveAssignments.map((person) => {
      const key = String(person?.id || "").trim();
      return {
        key,
        person,
        label: pickPersonalLabel(person),
      };
    });
  }, [peopleWithActiveAssignments]);

  const selectedOption = useMemo(() => {
    return inviteOptions.find((option) => option.key === selectedPersonKey) || null;
  }, [inviteOptions, selectedPersonKey]);

  const selectedPerson = useMemo(() => {
    if (!selectedPersonKey) return null;
    return people.find((person) => String(person?.id || "").trim() === selectedPersonKey) || null;
  }, [people, selectedPersonKey]);

  const selectedAssignment = useMemo(() => {
    if (!selectedPerson) return null;
    const personId = String(selectedPerson?.id || "").trim();
    return personId ? activeAssignmentByPersonId.get(personId) || null : null;
  }, [activeAssignmentByPersonId, selectedPerson]);

  const allowedEmails = useMemo(() => {
    const emails = new Set();

    for (const person of peopleWithActiveAssignments) {
      const email = normalizeEmail(person?.email);
      if (email) emails.add(email);
    }

    return emails;
  }, [peopleWithActiveAssignments]);

  const selectPlaceholder = useMemo(() => {
    if (loadingPeople) {
      return t("common.actions.loading", { defaultValue: "Cargando..." });
    }

    if (inviteOptions.length === 0) {
      return t("inviteTracker.empty.noActiveAssignments", {
        defaultValue: "Sin personal con asignación activa",
      });
    }

    return t("common.select", { defaultValue: "- Selecciona -" });
  }, [loadingPeople, inviteOptions.length, t]);

  // =========================
  // EFFECTS
  // =========================
  useEffect(() => {
    let cancelled = false;

    async function loadTrackerCount() {
      if (!orgId) {
        if (!cancelled) {
          setTrackerCount(null);
          setTrackerCountOrgId(null);
          setTrackerCountError(null);
          setLoadingTrackerCount(false);
        }
        return;
      }

      try {
        setLoadingTrackerCount(true);
        setTrackerCountError(null);
        setTrackerCount(null);
        setTrackerCountOrgId(null);

        const { count, error } = await supabase
          .from("memberships")
          .select("*", { count: "exact", head: true })
          .eq("org_id", orgId)
          .eq("role", "tracker")
          .is("revoked_at", null);

        if (error) throw error;

        if (count === null || !Number.isInteger(count) || count < 0) {
          throw new Error("El conteo de trackers no es un entero no negativo.");
        }

        if (!cancelled) {
          setTrackerCount(count);
          setTrackerCountOrgId(orgId);
        }
      } catch (error) {
        console.error("[invite-tracker] tracker count error", error);
        if (!cancelled) {
          setTrackerCount(null);
          setTrackerCountOrgId(null);
          setTrackerCountError(String(error?.message || error || "No se pudo cargar el conteo de trackers."));
        }
      } finally {
        if (!cancelled) {
          setLoadingTrackerCount(false);
        }
      }
    }

    loadTrackerCount();

    return () => {
      cancelled = true;
    };
  }, [orgId]);

  useEffect(() => {
    let cancelled = false;

    async function loadInviteSources() {
      setLoadingPeople(true);
      setErrMsg(null);
      setTrackerIdentityBlocked(false);

      if (!orgId) {
        if (!cancelled) {
          setPeople([]);
          setActiveAssignaciones([]);
          setLoadingPeople(false);
          setErrMsg(
            t("inviteTracker.errors.noOrg", {
              defaultValue: "No se pudo determinar la organización activa.",
            })
          );
        }
        return;
      }

      if (entitlementsLoading || entitlementsError || !canInviteTrackers) {
        if (!cancelled) {
          setPeople([]);
          setActiveAssignaciones([]);
          setLoadingPeople(false);
        }
        return;
      }

      try {
        const now = new Date().toISOString();

        const { data: assignacionesRows, error: assignacionesError } = await supabase
          .from("asignaciones")
          .select("id, org_id, personal_id, user_id, estado, status, start_time, end_time, is_deleted")
          .eq("org_id", orgId)
          .or("is_deleted.is.false,is_deleted.is.null")
          .or(`estado.eq.activa,status.eq.active,end_time.is.null,end_time.gte.${now}`)
          .limit(1000);

        if (assignacionesError) throw assignacionesError;
        if (cancelled) return;

        const activePersonIds = Array.from(
          new Set(
            (assignacionesRows || [])
              .map((assignment) => String(assignment?.personal_id || "").trim())
              .filter(Boolean)
          )
        );

        let peopleRows = [];

        if (activePersonIds.length > 0) {
          const { data, error } = await supabase
            .from("personal")
            .select("id, org_id, nombre, apellido, email, user_id, is_deleted")
            .eq("org_id", orgId)
            .or("is_deleted.is.false,is_deleted.is.null")
            .in("id", activePersonIds)
            .limit(500);

          if (error) throw error;
          peopleRows = data || [];
        }

        if (cancelled) return;

        setPeople(Array.isArray(peopleRows) ? peopleRows : []);
        setActiveAssignaciones(Array.isArray(assignacionesRows) ? assignacionesRows : []);
      } catch (error) {
        console.error("[invite-tracker] loadInviteSources error", error);
        if (cancelled) return;
        setPeople([]);
        setActiveAssignaciones([]);
        setErrMsg(String(error?.message || error));
      } finally {
        if (!cancelled) {
          setLoadingPeople(false);
        }
      }
    }

    loadInviteSources();

    return () => {
      cancelled = true;
    };
  }, [orgId, entitlementsLoading, entitlementsError, canInviteTrackers, t]);

  useEffect(() => {
    if (!selectedPerson) {
      setEmailInput("");
      setTrackerIdentityBlocked(false);
      return;
    }

    setEmailInput(String(selectedPerson?.email || ""));
    setTrackerIdentityBlocked(false);
  }, [selectedPerson]);

  // =========================
  // HELPERS / HANDLERS
  // =========================
  async function createPairingCodeForSelectedPerson() {
    setPairingLoading(true);
    setPairingError(null);
    setPairingCode("");
    setPairingCodeId("");
    setPairingCodeExpiresAt("");

    try {
      if (!orgId) throw new Error("No se pudo determinar la organización activa.");
      if (!selectedPerson?.id) throw new Error("Selecciona una persona.");

      const accessToken = await getAccessToken();
      if (!accessToken) throw new Error("No se encontró una sesión válida.");

      const response = await fetch(INVITE_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + accessToken,
          "x-user-jwt": accessToken,
        },
        body: JSON.stringify({
          action: "create_pairing_code",
          org_id: orgId,
          personal_id: selectedPerson.id,
          expires_hours: 72,
        }),
      });

      const result = await response.json().catch(() => null);

      if (!response.ok || !result?.ok || !result?.pairing_code) {
        throw new Error(result?.message || result?.error || "No se pudo generar el código.");
      }

      setPairingCode(result.pairing_code);
      setPairingCodeId(result.pairing_code_id || "");
      setPairingCodeExpiresAt(result.expires_at || "");
    } catch (error) {
      console.error("[invite-tracker] create pairing code failed", error);
      setPairingError(error?.message || String(error));
    } finally {
      setPairingLoading(false);
    }
  }
  async function getAccessToken() {
    const directToken = auth?.session?.access_token || auth?.access_token || auth?.token || null;
    if (directToken) return directToken;

    const { data, error } = await supabase.auth.getSession();
    if (error) throw error;

    return data?.session?.access_token || null;
  }

  function buildTrackerOnboardingMessage() {
    const email = normalizeEmail(emailInput) || "[correo]";

    return `Hola, primero debes instalar la app GeoField GPS y crear una cuenta usando este mismo correo: ${email}.

Después de crear la cuenta, entra una vez a la app. No tienes que configurar nada todavía.

Cuando ya hayas ingresado, me avisas y te envío la invitación para entrar como tracker.`;
  }

  async function copyTrackerOnboardingInstructions() {
    const text = buildTrackerOnboardingMessage();

    try {
      await navigator.clipboard.writeText(text);
      setOkMsg("Instrucciones copiadas para WhatsApp.");
      setErrMsg(null);
    } catch (error) {
      console.error("[invite-tracker] clipboard error", error);
      setErrMsg("No se pudo copiar el texto. Copia las instrucciones manualmente.");
    }
  }

  const recipientKey = JSON.stringify([orgId, normalizeEmail(emailInput)]);

  async function onSendInvite(event) {
    event?.preventDefault?.();
    if (inviteInFlight.current || sentRecipients.current.has(recipientKey)) return;
    inviteInFlight.current = true;

    setBusy(true);
    setOkMsg(null);
    setErrMsg(null);
    setTrackerIdentityBlocked(false);
    setInviteLink("");
    setInviteMeta(null);

    try {
      if (!orgId) {
        throw new Error(
          t("inviteTracker.errors.noOrg", {
            defaultValue: "No se pudo determinar la organización activa.",
          })
        );
      }

      if (permissionsLoading) {
        throw new Error(
          t("inviteTracker.org.syncing", {
            defaultValue: "Sincronizando organización y plan...",
          })
        );
      }

      if (entitlementsError) {
        throw new Error(
          t("inviteTracker.errors.planLoadFailed", {
            defaultValue: "No se pudo cargar la información del plan para esta organización.",
          })
        );
      }

      if (!canInviteTrackers) {
        throw new Error(
          t("inviteTracker.plan.permissionDisabledBody", {
            defaultValue: "Esta organización no puede invitar trackers en este momento.",
          })
        );
      }

      if (trackerCountError) {
        throw new Error(
          t("inviteTracker.errors.trackerCountFailed", {
            defaultValue: "No se pudo consultar el cupo de trackers disponible.",
          })
        );
      }

      if (loadingTrackerCount || trackerCount === null || trackerCountOrgId !== orgId) {
        throw new Error(
          t("inviteTracker.usage.loading", {
            defaultValue: "Calculando uso…",
          })
        );
      }

      if (!Number.isInteger(trackerCount) || trackerCount < 0) {
        throw new Error(
          t("inviteTracker.errors.sendFailed", {
            defaultValue: "No se pudo validar el cupo disponible.",
          })
        );
      }

      if (trackerLimitReached) {
        throw new Error(
          t("inviteTracker.usage.limitReached", {
            defaultValue: "Límite alcanzado",
          })
        );
      }

      if (!selectedPerson || !selectedAssignment?.id) {
        throw new Error(
          t("inviteTracker.errors.selectPerson", {
            defaultValue: "Selecciona una persona con asignación activa.",
          })
        );
      }

      const email = normalizeEmail(emailInput);

      if (!email || !isValidEmail(email)) {
        throw new Error(
          t("inviteTracker.errors.invalidEmail", {
            defaultValue: "Ingresa un email válido.",
          })
        );
      }

      if (!allowedEmails.has(email)) {
        throw new Error(
          t("inviteTracker.errors.emailMustMatchAssignedPerson", {
            defaultValue: "El email debe coincidir con el personal seleccionado con asignación activa.",
          })
        );
      }

      const accessToken = await getAccessToken();

      if (!accessToken) {
        throw new Error(
          t("inviteTracker.errors.noSession", {
            defaultValue: "No se encontró una sesión válida.",
          })
        );
      }

      const payload = {
        org_id: orgId,
        email,
        lang,
        personal_id: selectedPerson.id,
        assignment_id: selectedAssignment.id,
      };

      const response = await fetch(INVITE_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          "x-user-jwt": accessToken,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json().catch(() => null);

      if (!response.ok) {
        // Si el tracker aún no tiene identidad auth/personal.user_id,
        // no es un error técnico para el admin: mostramos onboarding accionable.
        if (
          result?.error === "tracker_identity_required" ||
          result?.error === "tracker_identity_missing"
        ) {
          setTrackerIdentityBlocked(true);
          setErrMsg(null);
          return;
        }
        throw new Error(
          result?.message ||
            result?.error ||
            t("inviteTracker.errors.sendFailed", {
              defaultValue: "No se pudo enviar la invitación.",
            })
        );
      }

      const inviteUrl = String(result?.invite_url || result?.inviteUrl || result?.url || "");

      sentRecipients.current.add(recipientKey);
      setSentRecipient(recipientKey);
      setInviteLink(inviteUrl);
      setInviteMeta({
        invite_id: result?.invite_id || result?.inviteId || "",
        created_at: result?.created_at || result?.createdAt || "",
        invite_url: inviteUrl,
      });

      setOkMsg(
        t("inviteTracker.success.inviteCreated", {
          defaultValue: "Invitación creada correctamente.",
        })
      );
    } catch (error) {
      console.error("[invite-tracker] send invite error", error);
      setErrMsg(String(error?.message || error));
    } finally {
      inviteInFlight.current = false;
      setBusy(false);
    }
  }

  const isSubmitDisabled =
    sentRecipients.current.has(recipientKey) ||
    busy ||
    loadingPeople ||
    loadingTrackerCount ||
    trackerCountUnavailable ||
    !orgId ||
    !hasActiveAssignmentsInOrg ||
    !selectedAssignment?.id ||
    permissionsLoading ||
    !!entitlementsError ||
    !canInviteTrackers ||
    trackerLimitReached;

  // =========================
  // GUARDS
  // =========================
  if (auth?.loading || entitlementsLoading || !entitlements) {
    return (
      <div className="mx-auto max-w-7xl space-y-6 p-4 md:p-6 lg:p-8">
        <div className="w-full max-w-2xl rounded-3xl border border-emerald-100 bg-white p-6 shadow-lg shadow-emerald-950/5">
          <h1 className="text-xl font-semibold text-gray-900">
            {t("inviteTracker.title", { defaultValue: "Invitar tracker" })}
          </h1>
          <p className="mt-3 text-sm text-emerald-700">
            {t("inviteTracker.org.syncing", {
              defaultValue: "Sincronizando organización y plan...",
            })}
          </p>
        </div>
      </div>
    );
  }

  if (inviteBlockedByPlan || inviteAccessBlock) {
    const reason = inviteAccessBlock || { title: t("inviteTracker.plan.permissionDisabledTitle", { defaultValue: "Permiso de invitación no habilitado." }), body: t("inviteTracker.plan.permissionDisabledBody", { defaultValue: "Esta organización no puede invitar trackers en este momento." }) };
    const blockTitle = reason.title;
    const blockBody = reason.body || reason.title;

    return (
      <div className="mx-auto max-w-7xl space-y-6 p-4 md:p-6 lg:p-8">
        <div className="w-full max-w-2xl rounded-3xl border border-emerald-100 bg-white p-6 shadow-lg shadow-emerald-950/5 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <h1 className="text-xl font-semibold text-gray-900">
              {t("inviteTracker.title", { defaultValue: "Invitar tracker" })}
            </h1>
            <button
              type="button"
              onClick={() => navigate("/tracker")}
              className="rounded-xl border px-3 py-2 text-sm hover:bg-emerald-50 text-emerald-900"
            >
              {t("inviteTracker.backToTracker", { defaultValue: "Volver a Tracker" })}
            </button>
          </div>

          <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-amber-900">
            <div className="text-base font-semibold">{blockTitle}</div>
            <div className="mt-3 text-sm">{blockBody}</div>
            {planCode && !inviteAccessBlock?.kind?.includes("loading") ? (
              <>
                <div className="mt-2 text-sm">
                  {t("inviteTracker.plan.detectedPlan", { defaultValue: "Plan detectado" })}: {" "}
                  <span className="font-semibold">{normalizePlanLabel(planCode)}</span>
                </div>
                <div className="mt-2 text-sm">
                  {t("inviteTracker.plan.statusLabel", { defaultValue: "Estado del plan" })}: {" "}
                  <span className="font-semibold">
                    {t(`status.${normalizedPlanStatus}`, { defaultValue: normalizedPlanStatus })}
                  </span>
                </div>
              </>
            ) : null}
            {isCancellationScheduled && inviteAccessBlock?.kind === "plan" ? (
              <div className="mt-2 text-xs text-amber-800">
                {t("inviteTracker.plan.cancellationScheduled", {
                  defaultValue: "Tu suscripción tiene una cancelación programada al final del período.",
                })}
              </div>
            ) : null}
          </div>

          {orgId && inviteAccessBlock?.kind === "plan" ? (
            <div className="rounded-xl border border-emerald-100 bg-white p-4">
              <div className="text-sm text-gray-700 mb-3">
                {t("billing.managePlan", { defaultValue: "Administrar plan" })}
              </div>
              <button
                type="button"
                onClick={() => navigate("/billing")}
                className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700"
              >
                {t("billing.goToBilling", { defaultValue: "Ir a Facturación" })}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  // =========================
  // JSX
  // =========================
  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 md:p-6 lg:p-8">

      <section className="relative overflow-hidden rounded-3xl border border-emerald-200/70 bg-gradient-to-br from-emerald-950 via-emerald-800 to-teal-700 px-5 py-6 text-white shadow-xl shadow-emerald-950/15 md:px-7 md:py-7">
        <div className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-white/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 left-10 h-56 w-56 rounded-full bg-lime-200/10 blur-3xl" />
        <div className="relative flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-3">
            <div className="inline-flex items-center rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-emerald-50">
              {t("inviteTracker.heroEyebrow", { defaultValue: "Invitaciones" })}
            </div>
            <div>
              <h1 className="text-3xl font-extrabold tracking-tight md:text-4xl">
                {t("inviteTracker.title", { defaultValue: "Invitar tracker" })}
              </h1>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-emerald-50/90">
                {t("inviteTracker.heroSubtitle", { defaultValue: "Invita a un trabajador con rol tracker a la organización correcta." })}
              </p>
            </div>
          </div>
        </div>
      </section>
      <div className="w-full max-w-2xl rounded-3xl border border-emerald-100 bg-white p-6 shadow-lg shadow-emerald-950/5">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-xl font-semibold text-gray-900">
            {t("inviteTracker.title", { defaultValue: "Invitar tracker" })}
          </h1>

          <button
            type="button"
            onClick={() => navigate("/tracker")}
            className="rounded-xl border px-3 py-2 text-sm hover:bg-emerald-50 text-emerald-900"
          >
            {t("inviteTracker.backToTracker", { defaultValue: "Volver a Tracker" })}
          </button>
        </div>

        {trackerIdentityBlocked ? (
          <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-4 text-amber-950">
            <div className="text-base font-semibold">
              {t("inviteTracker.identity.title", {
                defaultValue: "Esta persona aún no tiene cuenta",
              })}
            </div>

            <p className="mt-2 text-sm">
              {t("inviteTracker.identity.body", {
                defaultValue:
                  "Antes de enviar la invitación, el tracker debe crear su cuenta e ingresar una vez con el mismo correo.",
              })}
            </p>

            <ol className="mt-3 list-decimal space-y-1 pl-5 text-sm">
              <li>
                {t("inviteTracker.identity.stepInstall", {
                  defaultValue: "Instalar GeoField GPS.",
                })}
              </li>
              <li>
                {t("inviteTracker.identity.stepCreate", {
                  defaultValue: "Crear cuenta con este mismo correo.",
                })}
              </li>
              <li>
                {t("inviteTracker.identity.stepOpen", {
                  defaultValue: "Entrar una vez a la app.",
                })}
              </li>
              <li>
                {t("inviteTracker.identity.stepInvite", {
                  defaultValue: "Luego el administrador podrá enviar la invitación.",
                })}
              </li>
            </ol>

            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                className="rounded-xl border border-emerald-400 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-900 hover:bg-emerald-100"
                onClick={copyTrackerOnboardingInstructions}
              >
                {t("inviteTracker.identity.copyWhatsapp", {
                  defaultValue: "Copiar instrucciones para WhatsApp",
                })}
              </button>

              <button
                type="button"
                className="rounded-xl border border-emerald-400 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-900 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
                onClick={() => onSendInvite()}
                disabled={busy}
              >
                {busy
                  ? t("common.sending", { defaultValue: "Enviando..." })
                  : t("inviteTracker.identity.retry", {
                      defaultValue: "Revisar y enviar invitación nuevamente",
                    })}
              </button>
            </div>
          </div>
        ) : null}

        {errMsg ? (
          <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {errMsg}
          </div>
        ) : null}

        {okMsg ? (
          <div className="mt-4 rounded-xl border border-green-200 bg-green-50 p-3 text-sm text-green-700">
            {okMsg}
          </div>
        ) : null}

        <div className="mt-4 rounded-3xl border border-emerald-100 bg-gradient-to-r from-emerald-50 via-white to-teal-50 p-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-sm font-semibold text-emerald-950">
                {t("inviteTracker.usage.title", { defaultValue: "Uso de trackers" })}
              </div>
              <div className="mt-1 text-sm text-emerald-800">
                {t("inviteTracker.usage.current", {
                  defaultValue: "Usados: {{used}} / {{max}}",
                  used: trackerCount,
                  max: safeMaxTrackers,
                })}
              </div>
              <div className="mt-1 text-xs text-emerald-600">
                {t("inviteTracker.usage.plan", {
                  defaultValue: "Plan: {{plan}} · Estado: {{status}}",
                  plan: normalizePlanLabel(planCode),
                  status: String(normalizedPlanStatus || "-").toUpperCase(),
                })}
              </div>
              <div className="mt-1 text-xs text-emerald-600">
                {t("inviteTracker.usage.label", {
                  defaultValue: "Uso calculado: {{value}}",
                  value: trackersUsageLabel,
                })}
              </div>
            </div>

            <div className="text-sm">
              {loadingTrackerCount ? (
                <span className="text-emerald-600">
                  {t("inviteTracker.usage.loading", { defaultValue: "Calculando uso…" })}
                </span>
              ) : trackerLimitReached ? (
                <span className="font-medium text-amber-700">
                  {t("inviteTracker.usage.limitReached", { defaultValue: "Límite alcanzado" })}
                </span>
              ) : (
                <span className="font-medium text-emerald-700">
                  {t("inviteTracker.usage.available", { defaultValue: "Cupo disponible" })}
                </span>
              )}
            </div>
          </div>

          <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-emerald-100">
            <div
              className="h-full rounded-full bg-emerald-950 transition-all"
              style={{
                width:
                  safeMaxTrackers > 0
                    ? `${Math.min(100, Math.round((trackerCount / safeMaxTrackers) * 100))}%`
                    : "0%",
              }}
            />
          </div>

          {trackerLimitReached ? (
            <div className="mt-4 rounded-xl border border-emerald-100 bg-white p-4">
              <div className="text-sm text-emerald-800 mb-3">
                {t("inviteTracker.usage.upgradeLimit", {
                  defaultValue: "Tu plan actual llegó al límite. Actualiza para agregar más trackers.",
                })}
              </div>
              {orgId ? <UpgradeToProButton orgId={orgId} getAccessToken={getAccessToken} /> : null}
            </div>
          ) : null}
        </div>

        <form onSubmit={onSendInvite} className="mt-6 space-y-4">
          {inviteLink && inviteMeta ? (
            <div className="mt-4 flex flex-col gap-2">
              <div className="text-xs text-emerald-800 break-all">
                <b>Enlace de invitación:</b> <span>{inviteLink}</span>
              </div>
              <div className="text-xs text-emerald-800 break-all">
                <b>invite_id:</b> <span>{inviteMeta.invite_id}</span>
                <br />
                <b>created_at:</b> <span>{inviteMeta.created_at}</span>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="rounded bg-emerald-600 text-white px-3 py-1 text-xs font-semibold hover:bg-emerald-700"
                  onClick={() => {
                    if (inviteMeta?.invite_url) {
                      navigator.clipboard.writeText(inviteMeta.invite_url);
                    }
                  }}
                >
                  Copiar enlace
                </button>
                <button
                  type="button"
                  className="rounded bg-green-600 text-white px-3 py-1 text-xs font-semibold hover:bg-green-700"
                  onClick={() => {
                    if (inviteMeta?.invite_url) {
                      window.location.href = inviteMeta.invite_url;
                    }
                  }}
                >
                  Abrir enlace
                </button>
              </div>
            </div>
          ) : null}

          <div>
            <label className="block text-sm font-medium text-gray-900">
              {t("inviteTracker.selectPersonLabel", { defaultValue: "Selecciona una persona" })}
            </label>
            <select
              className="mt-1 w-full rounded-xl border px-3 py-2 bg-white text-gray-900"
              value={selectedPersonKey}
              onChange={(event) => {
                setSelectedPersonKey(event.target.value);
                setOkMsg(null);
                setErrMsg(null);
                setTrackerIdentityBlocked(false);
              }}
              disabled={loadingPeople || !hasActiveAssignmentsInOrg || inviteOptions.length === 0}
            >
              <option value="">{selectPlaceholder}</option>
              {inviteOptions.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="mt-2 text-xs text-emerald-700">
              {t("inviteTracker.onlyActiveAssignmentsNote", {
                defaultValue: "Solo aparecen personas con asignaciones vigentes (activas y dentro del período de tiempo).",
              })}
            </p>
            {selectedOption ? (
              <p className="mt-2 text-xs text-emerald-600">
                {t("inviteTracker.selectedPerson", {
                  defaultValue: "Seleccionado: {{label}}",
                  label: selectedOption.label,
                })}
              </p>
            ) : null}
          </div>

          <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="text-sm font-semibold text-emerald-950">
                  Codigo de emparejamiento Preview
                </div>
                <p className="mt-1 text-xs text-emerald-700">
                  Genera un codigo para que el tracker lo ingrese despues de iniciar sesion con Magic Link.
                </p>
              </div>

              <button
                type="button"
                onClick={createPairingCodeForSelectedPerson}
                disabled={pairingLoading || !selectedPerson?.id}
                className="rounded-xl bg-emerald-950 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-900 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {pairingLoading ? "Generando..." : "Generar codigo"}
              </button>
            </div>

            {pairingError ? (
              <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                {pairingError}
              </div>
            ) : null}

            {pairingCode ? (
              <div className="mt-4 rounded-xl border border-emerald-200 bg-white p-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
                  Codigo generado
                </div>

                <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <code className="rounded-xl bg-emerald-50 px-3 py-2 text-lg font-bold tracking-widest text-emerald-950">
                    {pairingCode}
                  </code>

                  <button
                    type="button"
                    className="rounded-xl bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700"
                    onClick={() => navigator.clipboard.writeText(pairingCode)}
                  >
                    Copiar codigo
                  </button>
                </div>

                <div className="mt-3 text-xs text-emerald-700">
                  {pairingCodeId ? (
                    <div>
                      <b>pairing_code_id:</b> <span className="break-all">{pairingCodeId}</span>
                    </div>
                  ) : null}
                  {pairingCodeExpiresAt ? (
                    <div>
                      <b>Expira:</b> <span>{pairingCodeExpiresAt}</span>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900">
              {t("inviteTracker.emailLabel", { defaultValue: "Correo electrónico del tracker" })}
            </label>
            <input
              className="mt-1 w-full rounded-xl border px-3 py-2 outline-none focus:ring bg-white text-gray-900"
              type="email"
              value={emailInput}
              onChange={(event) => {
                setEmailInput(event.target.value);
                setOkMsg(null);
                setErrMsg(null);
                setTrackerIdentityBlocked(false);
              }}
              placeholder={t("inviteTracker.emailPlaceholder", { defaultValue: "tracker@ejemplo.com" })}
              autoComplete="email"
            />
          </div>

          <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 mb-4">
            {t("inviteTracker.usage.trackers", {
              defaultValue: "Trackers usados: {{used}} / {{max}}",
              used: trackerCount,
              max: safeMaxTrackers,
            })}
          </div>

          <button
            type="submit"
            disabled={isSubmitDisabled}
            className={[
              "w-full rounded-xl px-4 py-3 text-sm font-semibold",
              isSubmitDisabled
                ? "bg-emerald-300 text-emerald-700 cursor-not-allowed"
                : "bg-black text-white hover:bg-emerald-950",
            ].join(" ")}
          >
            {busy
              ? t("common.sending", { defaultValue: "Enviando..." })
              : sentRecipient === recipientKey ? t("inviteTracker.success.inviteCreated", { defaultValue: "Invitación creada correctamente." }) : t("inviteTracker.sendInvite", { defaultValue: "Enviar invitación" })}
          </button>
        </form>
      </div>
    </div>
  );
}
